import { HttpClient } from '@angular/common/http';
import { Injectable, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments';
import { LoginRequest, LoginResponse, PlatformAdminSession } from '../models/auth.model';
import {
  AcceptPlatformAdminInviteRequest,
  CurrentPlatformAdmin,
  PlatformAdminInviteInfo,
} from '../models/platform-admin.model';

const SESSION_STORAGE_KEY = 'boltconsole.session';

/**
 * Auth for the platform-admin ("super admin of the whole Bolt SaaS") console.
 *
 * The JWT is deliberately NOT persisted to localStorage - this is a high-privilege
 * account with no refresh-token flow, so we keep it in memory + sessionStorage only
 * (survives a page refresh within the same tab, but never lingers past the browser
 * session). Each token is minted for 60 minutes; a few minutes before it expires we
 * silently call /auth/renew for a fresh one (see scheduleSessionMaintenance) so an admin
 * actively working - reviewing the audit log, mid-impersonation - isn't bounced out from
 * under them. Renew only works with a still-valid token (no rotation/refresh-token state
 * to steal) and re-checks IsActive/Role server-side, so a deactivated admin can't renew
 * their way past that. If the tab sits idle long enough that renew itself lapses, or the
 * server rejects it, the user is sent back to /login exactly as before this existed.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly sessionSignal = signal<PlatformAdminSession | null>(this.restoreSession());
  private maintenanceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Renew this long before the current token would otherwise expire. */
  private static readonly RENEW_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

  readonly isAuthenticated = computed(() => {
    const session = this.sessionSignal();
    return !!session && session.expiresAt > Date.now();
  });

  readonly fullName = computed(() => this.sessionSignal()?.fullName ?? '');
  readonly email = computed(() => this.sessionSignal()?.email ?? '');
  readonly role = computed(() => this.sessionSignal()?.role ?? null);
  readonly permissions = computed(() => this.sessionSignal()?.permissions ?? []);
  readonly platformAdminId = computed(() => this.sessionSignal()?.platformAdminId ?? null);

  /** UI-convenience checks only — the backend re-validates every permission on every request
   * regardless of what these say, so a stale cached value here can never grant real access. */
  readonly isSuperAdmin = computed(() => this.role() === 'SuperAdmin');
  readonly canManageCompanies = computed(() => this.permissions().includes('Companies.Manage'));
  readonly canManageConsoleAdmins = computed(() => this.permissions().includes('ConsoleAdmins.Manage'));

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {
    this.scheduleSessionMaintenance();
  }

  login(request: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${environment.apiBaseUrl}/auth/login`, request)
      .pipe(
        tap((response) => {
          if (response.success) {
            this.setSession({
              accessToken: response.accessToken,
              fullName: response.fullName,
              email: response.email,
              expiresAt: Date.now() + response.expiresIn * 1000,
            });
            this.refreshMe();
          }
        }),
      );
  }

  /**
   * Fetches this admin's role/permissions from the server and merges them into the current
   * session. Called right after login, and safe to call again any time (e.g. the console-admins
   * page could call it defensively) — never trust the JWT claims client-side for UI decisions.
   */
  refreshMe(): void {
    this.http.get<CurrentPlatformAdmin>(`${environment.apiBaseUrl}/auth/me`).subscribe({
      next: (me) => {
        const session = this.sessionSignal();
        if (!session) {
          return;
        }
        this.setSession({
          ...session,
          platformAdminId: me.platformAdminId,
          role: me.role,
          permissions: me.permissions,
        });
      },
      error: () => {
        // A failure here rides along with the normal 401 interceptor handling elsewhere; nothing
        // extra to do — worst case the UI is momentarily under-permissioned until the next call.
      },
    });
  }

  /** Public (pre-login) lookup for the "set your password" page. */
  getInviteInfo(token: string): Observable<PlatformAdminInviteInfo> {
    return this.http.get<PlatformAdminInviteInfo>(
      `${environment.apiBaseUrl}/auth/invite/${encodeURIComponent(token)}`,
    );
  }

  acceptInvite(request: AcceptPlatformAdminInviteRequest): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiBaseUrl}/auth/accept-invite`, request);
  }

  logout(navigateToLogin = true): void {
    this.clearSession();
    if (navigateToLogin) {
      this.router.navigate(['/login']);
    }
  }

  getToken(): string | null {
    const session = this.sessionSignal();
    if (!session) {
      return null;
    }
    if (session.expiresAt <= Date.now()) {
      this.clearSession();
      return null;
    }
    return session.accessToken;
  }

  private setSession(session: PlatformAdminSession): void {
    this.sessionSignal.set(session);
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    } catch {
      // sessionStorage unavailable (private browsing lockdown, etc.) - in-memory session still works.
    }
    this.scheduleSessionMaintenance();
  }

  private clearSession(): void {
    this.sessionSignal.set(null);
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      // ignore
    }
    if (this.maintenanceTimer) {
      clearTimeout(this.maintenanceTimer);
      this.maintenanceTimer = null;
    }
  }

  private restoreSession(): PlatformAdminSession | null {
    try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const session = JSON.parse(raw) as PlatformAdminSession;
      if (!session.accessToken || session.expiresAt <= Date.now()) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        return null;
      }
      return session;
    } catch {
      return null;
    }
  }

  private scheduleSessionMaintenance(): void {
    if (this.maintenanceTimer) {
      clearTimeout(this.maintenanceTimer);
      this.maintenanceTimer = null;
    }
    const session = this.sessionSignal();
    if (!session) {
      return;
    }
    const msRemaining = session.expiresAt - Date.now();
    if (msRemaining <= 0) {
      this.logout(false);
      return;
    }

    const msUntilRenew = msRemaining - AuthService.RENEW_BEFORE_EXPIRY_MS;
    if (msUntilRenew <= 0) {
      // Already inside the renewal window (e.g. restoring a session close to expiry) - renew now.
      this.renew();
      return;
    }
    this.maintenanceTimer = setTimeout(() => this.renew(), msUntilRenew);
  }

  /** Silently mints a fresh token a few minutes before the current one expires. On failure, falls
   * back to a hard logout timer at the ORIGINAL expiry (unchanged) rather than retrying forever -
   * worst case behaves exactly like this feature never existed. */
  private renew(): void {
    const session = this.sessionSignal();
    if (!session) {
      return;
    }

    this.http.post<LoginResponse>(`${environment.apiBaseUrl}/auth/renew`, {}).subscribe({
      next: (response) => {
        if (response.success) {
          this.setSession({ ...session, accessToken: response.accessToken, expiresAt: Date.now() + response.expiresIn * 1000 });
        } else {
          this.logout();
        }
      },
      error: () => {
        const msRemaining = session.expiresAt - Date.now();
        if (msRemaining <= 0) {
          this.logout();
          return;
        }
        this.maintenanceTimer = setTimeout(() => this.logout(), msRemaining);
      },
    });
  }
}
